import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, signal, ViewChild, ViewEncapsulation} from '@angular/core';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { divIcon, marker, Map, SVG, popup, latLng } from 'leaflet';
import { Subject } from 'rxjs';
import { LeafletConfigService } from '@app/shared/services/leaflet-config.service';
import vectorTileLayer from 'leaflet-vector-tile-layer';
import { featureLayerBase, defaultFeatureLayer } from 'leaflet-vector-tile-layer';
import { environment } from '@environments/environment';
import { AuthService } from '@app/services/auth.service';
import { take } from 'rxjs/operators';
import { Router, RouterOutlet } from '@angular/router';
import {SearchControlComponent} from "@app/pages/map/controls/search-control/search-control.component";
import {MatToolbar} from "@angular/material/toolbar";
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserDropdownComponent } from '@app/components/user-dropdown/user-dropdown.component';
import { SearchControlService } from './controls/services/search-control.service';
import { ConnectorChargingStatusComponent } from '@app/components/charger/connector-charging-status/connector-charging-status.component';
import { CommonModule } from '@angular/common';
import { DevcycleService, FEATURE_FLAGS } from '@app/services/devcycle.service';
import { AdStripComponent } from '@app/shared/components/ad-strip/ad-strip.component';
// TODO: AdPinPopupComponent exists but is unused — currently using inline HTML for Leaflet popups.
// Refactor to use a ComponentFactoryResolver-driven popup for proper Angular bindings.
// import { AdPinPopupComponent } from '@app/shared/components/ad-pin/ad-pin.component';
import { AdService, ServedAd } from '@app/services/ad.service';
import { TourAnchorMatMenuDirective } from 'ngx-ui-tour-md-menu';
import { tourStation } from '@app/shared/utils/tour';

const DEFAULT_ZOOM = 14;

@Component({
  selector: 'app-gc-map',
  standalone: true,
  imports: [LeafletModule, RouterOutlet, SearchControlComponent, MatToolbar, MatButtonModule, MatIconModule, MatTooltipModule, UserDropdownComponent, ConnectorChargingStatusComponent, CommonModule, TourAnchorMatMenuDirective, AdStripComponent],
  templateUrl: './map.component.html',
  styleUrl: './map.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class MapComponent implements OnDestroy, OnInit {
  advertiseUrl = environment.advertiseUrl;
  public map: Map | undefined = undefined;
  public zoom!: number;
  private onDestroy$ = new Subject();
  @Input() mapId = 'map-container';
  @ViewChild('jumpToMyLocationButton') jumpToMyLocationButton;

  showChargerStatus = signal(false);
  markerTourPoint;
  private _adPinMarkers: { [key: string]: any } = {};
  private adPinDebounce: any = null;

  private mapResizeObserver = new ResizeObserver(() => {
    this.map?.invalidateSize();
  });

  options: any;
  constructor(
    private leafletConfig: LeafletConfigService,
    private authService: AuthService,
    private router: Router,
    private snackBar: MatSnackBar,
    private searchControlService: SearchControlService,
    private devcycleService: DevcycleService,
    private adService: AdService,
  ) {
    this.options = {
      ...this.leafletConfig.defaultMapOptions,
      zoomControl: true,
    };
  }

  ngOnInit(): void {
    this.showChargerStatus.set(this.devcycleService.getFeatureFlag(FEATURE_FLAGS.CHARGER_STATUS_ENABLE, false));
    this.devcycleService.onVariableUpdated(FEATURE_FLAGS.CHARGER_STATUS_ENABLE, (key, value) => {
      this.showChargerStatus.set(value);
    });

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        if (position) {
          this.searchControlService.userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          // Set the map to the user's location
          const userLocationMarker = divIcon({
            className: 'user-location-marker',
            html: '<div class="user-location-marker-icon"></div>',
            iconSize: [15, 15],
          })
          marker([this.searchControlService.userLocation.lat, this.searchControlService.userLocation.lng], { icon: userLocationMarker }).addTo(this.map);
          this.jumpToMyLocation();
        } else {
          this.snackBar.open('GitCharger does not have permission to use your location.', undefined, {
            duration: 3000,
          });
        }
      }, () => {
        this.snackBar.open('GitCharger does not have permission to use your location.', undefined, {
          duration: 3000,
        });
      });
    }

  }

  ngOnDestroy(): void {
    try {
      this.map?.clearAllEventListeners();
      this.map?.remove();
      this.mapResizeObserver.disconnect();
      this.onDestroy$.next(true);
      this.onDestroy$.complete();
    } catch {
      // ignore
    }
  }

  onMapReady(map: Map) {
    this.map = map;
    this.zoom = map.getZoom();
    this.addTileLayerWithToken();

    // Load sponsored pins on map ready and on move
    this.loadAdPins();
    map.on('moveend', () => this.loadAdPins());
  }

  private addTileLayerWithToken() {
    const tileLayerUrl = `${environment.apiUrl}/tiles/{z}/{x}/{y}`;

    this.authService.getIdToken().pipe(take(1)).subscribe(
      token => {
        if (token) {
          this.addTileLayer(tileLayerUrl, token);
        } else {
          console.error('Token not available');
        }
      },
      error => {
        console.error('Error fetching token', error);
      }
    );
  }

  private featureToLayer(feature, layerName, pxPerExtent, options) {
    // Construct a base feature layer.
    const self = featureLayerBase(feature, layerName, pxPerExtent, options);

    // Compose this feature layer of two sub-layers, one for the visible
    // line controlled by `options` and a second controlled by the path
    // options contained in `options.interaction`. Both will share the same
    // path geometry.
    self.visibleLine = defaultFeatureLayer(
      feature,
      layerName,
      pxPerExtent,
      options
    );
    self.interactionLine = defaultFeatureLayer(
      feature,
      layerName,
      pxPerExtent,
      options.interaction
    );

    // Place the two layers in an SVG group.
    const group = SVG.create("g");
    group.appendChild(self.visibleLine.graphics);
    group.appendChild(self.interactionLine.graphics);
    self.graphics = group;

    // Setting of style is delegated to the sub layers.
    self.setStyle = function setStyle(style) {
      self.visibleLine.setStyle(style);
      self.interactionLine.setStyle(style.interaction);
    };

    // Initial setup of this feature layer.
    self.applyOptions(options);

    return self;
  }

  private iconUrl(count) {
    let iconUrl = 'assets/map/icons/DefaultMarker.svg';
    //TODO based on power level
    if(count == 1) {
      iconUrl = 'assets/map/icons/DefaultMarker.svg'
    } else {
      iconUrl = `data:image/svg+xml;base64,${btoa(this.generateSvgIcon(count))}`;
    }

    return iconUrl;
  }

  private addTileLayer(tileLayerUrl: string, token: string) {
    const stationsLayer = vectorTileLayer(tileLayerUrl, {
      fetchOptions: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      },
      interactive: true,
      getFeatureId: (feature) => feature.properties.id,
      featureToLayer: this.featureToLayer,
      vectorTileLayerStyles: {
        stations: (properties, zoom) => {
          const count = properties.count;
          // Define default min and max radius values
          let minRadius = 20;
          let maxRadius = 35;

          // Adjust radius based on zoom level
          if (zoom < 8) {
            minRadius = 11;
            maxRadius = 20;
          }

          // Calculate the radius
          let radius = Math.min(maxRadius, minRadius + Math.log(count) * 2);

          // Further adjust radius for count === 1 based on zoom level
          if (count === 1) {
            radius = Math.min(maxRadius, minRadius + zoom / 2);
          }
         // console.log(radius, zoom);

          const iconUrl = this.iconUrl(count);
          //console.log(properties);
          return {
            icon: divIcon({
              iconUrl: iconUrl,
              iconSize: [radius * 2, radius * 2],
              iconAnchor: [radius, radius],
            }),
            interaction: {
              radius,
              fillOpacity: 0,
              stroke: false,
              weight: 2,
              opacity: 1,
            },
          };
        },
      }
    });

    // NOTE: Assign a name to the layer, so we can access this later.
    (stationsLayer as any)._layerName = this.searchControlService.STATION_LAYER_NAME;

   /* stationsLayer.on('mouseover', (e) => {
      const layer = e.layer;
      const latlng = e.latlng || (e.originalEvent && e.originalEvent.latlng);
      if (layer && layer.properties && layer.properties.name && latlng) {
        layer.bindTooltip(layer.properties.name, { permanent: false, className: 'station-tooltip' }).openTooltip(latlng);
      }
    });

    stationsLayer.on('mouseout', (e) => {
      const layer = e.layer;
      if (layer && layer.unbindTooltip) {
        layer.unbindTooltip();
      }
    });*/

    stationsLayer.on('click', (e) => {
      const layer = e.layer;
      const latlng = e.latlng || (e.originalEvent && e.originalEvent.latlng);

      this.map.setView([e?.layer?.properties?.lat, e?.layer?.properties?.lng], DEFAULT_ZOOM);
      if(layer && layer.properties.count > 1) {
        this.map.setZoomAround(latlng, layer.properties.expansionZoom)
      } else {

          //const popupContent = `<strong>${layer.properties.name}</strong><br>Some more info here.`;
          //layer.bindPopup(popupContent).openPopup(latlng);
        this.router.navigate([`/station/${layer.properties.id}`])

      }
    });

    stationsLayer.on('tileerror', (error) => {
      if (error.error && error.error.message.includes('401')) {
        // Token expired or unauthorized, refresh token and retry
        this.authService.getIdToken().pipe(take(1)).subscribe(
          newToken => {
            if (newToken) {
              this.map?.removeLayer(stationsLayer);
              this.addTileLayer(tileLayerUrl, newToken);
            } else {
              console.error('Failed to refresh token');
            }
          },
          tokenError => {
            console.error('Error refreshing token', tokenError);
          }
        );
      }
    });

    this.map?.addLayer(stationsLayer);
  }

  generateSvgIcon(count: number): string {
    const radius = 20;
    const diameter = radius * 2;
    return `
    <svg width="${diameter}" height="${diameter}" viewBox="0 0 ${diameter} ${diameter}" xmlns="http://www.w3.org/2000/svg">
      <circle cx="${radius}" cy="${radius}" r="${radius}" fill="#05BA00" />
      <text x="50%" y="50%" dy=".3em" text-anchor="middle" font-size="12" fill="white">${count}</text>
    </svg>
  `;
  }

  onMapClick() {
    this.searchControlService.removeLocationMarkers();

    if (window.location.pathname.startsWith('/station')) {
      this.router.navigate(['/']);
    }
  }

  isLocationAvailable(): boolean {
    return !!this.searchControlService.userLocation;
  }

  jumpToMyLocation(): void {
    this.map.setView({
      lat: this.searchControlService.userLocation.lat,
      lng: this.searchControlService.userLocation.lng,
    }, DEFAULT_ZOOM);
    this.isMyLocationVisible();
  }

  isMyLocationVisible() {
    const visible = this?.searchControlService?.userLocation?.lat === this.map.getCenter().lat && this?.searchControlService?.userLocation?.lng === this.map.getCenter().lng && this.map.getZoom() === DEFAULT_ZOOM;
    if (visible) {
      this.jumpToMyLocationButton._elementRef.nativeElement.classList.add('active');
    } else {
      this.jumpToMyLocationButton._elementRef.nativeElement.classList.remove('active');
    }
  }

  onMapMove() {
    this.jumpToMyLocationButton._elementRef.nativeElement.classList.remove('active');
  }

  // ─── Sponsored Map Pins ───────────────────────────────────────────────

  private static readonly AD_PIN_MIN_ZOOM = 10;

  private loadAdPins() {
    if (!this.map || !this.adService.isPlacementEnabled('mapPin')) return;

    // Hide pins when zoomed out too far
    if (this.map.getZoom() < MapComponent.AD_PIN_MIN_ZOOM) {
      Object.values(this._adPinMarkers).forEach(m => this.map?.removeLayer(m));
      this._adPinMarkers = {};
      return;
    }

    // Debounce — don't fire on every pixel of a pan
    clearTimeout(this.adPinDebounce);
    this.adPinDebounce = setTimeout(() => this.fetchAdPins(), 500);
  }

  private fetchAdPins() {
    if (!this.map) return;
    const bounds = this.map.getBounds();

    this.adService.getMapPins({
      neLat: bounds.getNorthEast().lat,
      neLng: bounds.getNorthEast().lng,
      swLat: bounds.getSouthWest().lat,
      swLng: bounds.getSouthWest().lng,
    }).subscribe(pins => {
      // Only add new pins — skip ones already on the map
      const newPinIds = new Set(pins.map(p => p.adId));

      // Remove pins no longer in the response
      Object.keys(this._adPinMarkers).forEach(id => {
        if (!newPinIds.has(id)) {
          this.map?.removeLayer(this._adPinMarkers[id]);
          delete this._adPinMarkers[id];
        }
      });

      // Add pins not yet on the map
      for (const pin of pins) {
        if (pin.latitude == null || pin.longitude == null) continue;
        if (this._adPinMarkers[pin.adId]) continue;
        this.addSponsoredPin(pin);
      }
    });
  }

  private addSponsoredPin(ad: ServedAd) {
    const logoHtml = ad.logoUrl
      ? `<img src="${ad.logoUrl}" style="width:24px;height:24px;border-radius:50%;object-fit:cover;" />`
      : `<svg viewBox="0 0 24 24" style="width:18px;height:18px;fill:white"><path d="M7 2v11h3v9l7-12h-4l4-8z"/></svg>`;

    const icon = divIcon({
      className: 'sponsored-map-pin',
      html: `
        <div class="sponsored-pin-outer">
          <div class="sponsored-pin-ring"></div>
          <div class="sponsored-pin-circle">${logoHtml}</div>
        </div>
      `,
      iconSize: [48, 48],
      iconAnchor: [24, 24],
      popupAnchor: [0, -28],
    });

    const m = marker([ad.latitude!, ad.longitude!], { icon }).addTo(this.map!);

    // Build popup HTML
    const imageSection = ad.imageUrl
      ? `<img src="${ad.imageUrl}" style="width:100%;height:110px;object-fit:cover;">`
      : `<div style="width:100%;height:80px;background:linear-gradient(135deg,#1565C0,#0D47A1);display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:16px;">${this.escapeHtml(ad.headline || '')}</div>`;

    const popupHtml = `
      <div style="width:240px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
        <div style="position:relative;overflow:hidden;border-radius:8px 8px 0 0;">
          <span style="position:absolute;top:6px;left:6px;background:rgba(0,0,0,0.5);color:white;font-size:9px;font-weight:600;padding:2px 6px;border-radius:3px;">Sponsored</span>
          ${imageSection}
        </div>
        <div style="padding:10px 12px;text-align:center;">
          <div style="font-size:13px;font-weight:600;color:#1a1a1a;line-height:1.3;">${this.escapeHtml(ad.headline || '')}</div>
          <div style="font-size:11px;color:#6B7280;margin-top:3px;line-height:1.4;">${this.escapeHtml(ad.description || '')}</div>
        </div>
        <div style="display:flex;align-items:center;justify-content:center;gap:8px;padding:0 12px 10px;">
          <span style="font-size:10px;color:#9CA3AF;">${this.escapeHtml(ad.displayUrl || '')}</span>
          <a href="${ad.destinationUrl}" target="_blank" style="font-size:11px;font-weight:600;color:#1565C0;padding:4px 10px;border:1px solid #1565C0;border-radius:5px;text-decoration:none;">Learn More</a>
        </div>
      </div>
    `;

    m.bindPopup(popup({ maxWidth: 260, className: 'leaflet-ad-pin-popup', closeButton: false }).setContent(popupHtml));

    // Record impression when popup opens (user taps pin), then wire click tracking
    m.on('popupopen', () => {
      this.adService.recordPinImpression(ad.adId, ad.token).subscribe(impressionId => {
        if (impressionId) {
          ad.impressionId = impressionId;
        }
        const container = m.getPopup()?.getElement();
        const link = container?.querySelector('a[target="_blank"]');
        link?.addEventListener('click', () => this.adService.recordClick(ad));
      });
    });

    this._adPinMarkers[ad.adId] = m;
  }

  private escapeHtml(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}
